"use client";

import React from "react";
import { Clock, CircleQuestionMark as HelpCircle, MapPin, PhoneCall, ShieldCheck, Wrench } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "Do you provide emergency plumbing across all of Lovejoy, GA?",
  answer: "Yes. We cover the entire City of Lovejoy as well as neighboring Clayton and Henry County borders. Whether you are off Tara Boulevard, McDonough Road, or near Talmadge Road, our technicians are dispatched locally for fast arrival."
}, {
  id: "item-2",
  question: "How fast can a plumber arrive at my Lovejoy home during an emergency?",
  answer: "For critical emergencies like burst supply pipes, active sewage backups, or major water heater floods, our typical response time in Lovejoy is between 45 to 90 minutes depending on traffic and current dispatch queues."
}, {
  id: "item-3",
  question: "What plumbing services are available in Lovejoy?",
  answer: "We provide complete residential and light commercial plumbing solutions in Lovejoy. This includes emergency leak repairs, tankless and tank water heater repair and installation, drain clearing, toilet rebuilding, sewer line inspection and repair, and whole-home repiping."
}, {
  id: "item-4",
  question: "Are your technicians available on weekends and holidays?",
  answer: "Yes. Plumbing emergencies do not wait for business hours. We operate 24 hours a day, 7 days a week, 365 days a year without shutting down on weekends or national holidays."
}, {
  id: "item-5",
  question: "How do I request emergency plumbing service in Lovejoy?",
  answer: "For urgent situations, the quickest way to get help is to call our direct dispatch line. Our team will guide you on how to safely shut off your main water valve and immediately route a licensed technician to your location."
}, {
  id: "item-6",
  question: "What are your specific service area boundaries around Lovejoy?",
  answer: "Our Lovejoy coverage spans from the Jonesboro city border down through Lovejoy proper, extending east toward Hampton and west toward Fayetteville edges. If you are unsure whether you fall within our immediate route, give us a quick call to confirm dispatch availability."
}];
const highlights = [{
  title: "24/7 Fast Dispatch",
  desc: "Plumbers ready on call day, night, and holidays.",
  icon: Clock
}, {
  title: "Lovejoy & Surrounds",
  desc: "Full coverage across Tara Blvd & local neighborhoods.",
  icon: MapPin
}, {
  title: "Licensed & Insured",
  desc: "Guaranteed workmanship and upfront quotes.",
  icon: ShieldCheck
}];
export default function LovejoyFaq() {
  return <section id="lovejoyservicefaq" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Context & Quick Contact Card */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div>
              <Badge variant="secondary" className="mb-3">
                <HelpCircle className="size-3.5" />
                <span>Lovejoy Coverage FAQ</span>
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight text-foreground">
                Frequently Asked Questions in Lovejoy, GA
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Find clear answers about our emergency plumbing response times, service area boundaries, and available repair solutions in Lovejoy.
              </p>
            </div>

            {/* Quick Stats / Guarantee Badges */}
            <div className="grid grid-cols-1 gap-3.5 pt-2">
              {highlights.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.title} data-index={i} className="flex items-start gap-3.5 p-3.5 rounded-lg border border-border bg-card text-card-foreground shadow-2xs">
                    <div className="rounded-md bg-primary/10 text-primary p-2 shrink-0">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground font-default">
                        {item.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Contact Callout Card */}
            <div className="rounded-xl border border-border bg-muted/50 p-6 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-accent/20 p-2.5 text-accent-foreground">
                  <PhoneCall className="size-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold font-default text-foreground">
                    Have an urgent plumbing emergency?
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Speak with our local dispatch team immediately.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button asChild size="default" className="w-full sm:w-auto">
                  <Link to="/contact/">Request Service</Link>
                </Button>
                <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
                  <Link to="/emergency-plumbing/">
                    <Wrench className="size-4" />
                    <span>Emergency Info</span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Accordion */}
          <div className="lg:col-span-7">
            <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-sm">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                {faqList.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b border-border py-1 first:pt-0 last:border-b-0">
                    <AccordionTrigger className="text-base sm:text-lg font-semibold text-foreground hover:no-underline py-4 text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}